<template>
  <div>
      <div class="card col-12 p-0">
        <form class="form-horizontal">
          <div class="card-body">
            <el-radio-group id="marker-selection" v-model="selectedMarkerMode" @change="redrawMarkers">
              <el-radio-button :label="BY_PROJECT"></el-radio-button>
              <el-radio-button :label="BY_INSPECTION"></el-radio-button>
            </el-radio-group>

            <div ref="routingMap" class="map map-big" style="min-height:625px"></div>
            <div v-if="!errorsDismissed && (missingCoordinatesErrorsReactive.size > 0 || cityCoordinatesErrorsReactive.size > 0)"
                 id="marker-error-banner"
                 class="bg-danger pt-3 pb-2 rounded d-flex flex-column justify-content-between align-items-center"
            >
              <span v-if="missingCoordinatesErrorsReactive.size > 0" style="font-size: medium;">
                <strong>{{ missingCoordinatesErrorsReactive.size }} markers could not be created (No project lat/lng)</strong>
              </span>
              <span v-if="cityCoordinatesErrorsReactive.size > 0" style="font-size: medium;">
                <strong>{{ cityCoordinatesErrorsReactive.size }} markers were created using City lat/lng</strong>
              </span>
              <div class="d-flex justify-content-between pt-2 w-75">
                <button class="btn btn-sm btn-primary" @click="updateMissingProjectCoordinates" :disabled="repairingErrors">
                  <span v-if="!repairingErrors">Try to Repair</span>
                  <div v-else class="d-flex justify-content-center">
                    <span>Repairing . . .&nbsp;</span>
                    <img class="icon"
                         style="width: 12px; height: 12px"
                         src="static/img/loading-1.gif"
                    />
                  </div>
                </button>
                <button class="btn btn-sm btn-secondary" @click="errorsDismissed = true" :disabled="repairingErrors">Dismiss</button>
              </div>
          </div>
          </div>
        </form>
      </div>
  </div>
</template>

<script>
import Vue from 'vue'
import Constants from '@/utilities/constants'
import {RadioButton, RadioGroup} from 'element-ui'
import InspectionMarkerInfoWindowContent from '@/components/Dashboard/Views/InspectorRouting/InspectionMarkerInfoWindowContent'
import ProjectMarkerInfoWindowContent from '@/components/Dashboard/Views/InspectorRouting/ProjectMarkerInfoWindowContent'
import {mapGetters} from 'vuex'
import {commit, dispatch} from 'vuex-pathify'
import {Loader} from 'google-maps'
import {MarkerClusterer} from '@googlemaps/markerclusterer'
import {OK} from 'http-status-codes'

export default {
  components: {
    elRadioButton: RadioButton,
    elRadioGroup: RadioGroup
  },
  computed: {
    ...mapGetters({
      inspectionsByInspector: 'inspectorRouting/getInspectionsByInspector',
      inspectionsByProject: 'inspectorRouting/getInspectionsByProject',
      solverParams: 'inspectorRouting/solverParams'
    }),
    missingCoordinatesErrorsReactive() {
      return this.missingCoordinatesErrorsChangeTracker && this.missingCoordinatesErrors
    },
    cityCoordinatesErrorsReactive() {
      return this.cityCoordinatesErrorsChangeTracker && this.cityCoordinatesErrors
    }
  },
  data() {
    return {
      constants: Constants,
      BY_PROJECT: "Mark Projects",
      BY_INSPECTION: "Mark Inspections",
      selectedMarkerMode: null,
      prevMarkerMode: null, // need this to clear markers
      google: null,
      map: null,
      activeInfoWindow: null,
      defaultZoom: 7,
      mapCenter: {lat: Constants.ATS.HQ.COORDS.LAT + 1, lng: Constants.ATS.HQ.COORDS.LNG + 0.8}, //at zoom 7 fits all major cities
      projectMarkersArray: [],
      projectMarkersToBounce: [],
      inspectionMarkersArray: [],
      inspectionMarkersToBounce: [],
      markerClusterer: null,
      markerAnimation: null,
      missingCoordinatesErrors: new Set,
      missingCoordinatesErrorsChangeTracker: 0,
      cityCoordinatesErrors: new Set,
      cityCoordinatesErrorsChangeTracker: 0,
      repairingErrors: false,
      errorsDismissed: false
    }
  },
  methods: {
    async initGoogleMap() {
      if (!this.$refs.routingMap) {
        return
      }
      const apiKey = this.$store.get('config/byName', Constants.CONFIGS.GOOGLE_MAPS_API_KEY)
      const loader = new Loader(apiKey, {})
      const google = await loader.load()
      this.google = google
      this.markerAnimation = google.maps.Animation.BOUNCE

      const mapOptions = {
        zoom: this.defaultZoom,
        center: new google.maps.LatLng(this.mapCenter),
      }

      this.map = new google.maps.Map(this.$refs.routingMap, mapOptions)
      this.loadClusteredMarkers()
      this.markerClusterer = new MarkerClusterer({map:this.map})
      this.redrawMarkers()
    },
    loadClusteredMarkers() {
      // load Project Markers
      this.projectMarkersArray = []
      for (const project of Object.values(this.inspectionsByProject)) {
        const marker = this.createProjectMarker(project)
        if(marker) {
          this.projectMarkersArray.push(marker)
        }

      }

      // load Inspection Markers
      this.inspectionMarkersArray = []
      for (const inspector of Object.values(this.inspectionsByInspector)) {
        for (const inspection of inspector.inspections) {
          const marker = this.createInspectionMarker(inspector, inspection)
          if (marker) {
            this.inspectionMarkersArray.push(marker)
          }
        }
      }
    },
    redrawMarkers() {
      if (this.selectedMarkerMode === this.BY_PROJECT) {

        this.markerClusterer.clearMarkers()
        this.markerClusterer.addMarkers(this.projectMarkersArray)

        for (const marker of this.projectMarkersToBounce) {
          this.setMarkerBounce(marker)
        }
        for (const marker of this.inspectionMarkersToBounce) {
          marker.setVisible(false)
        }

      } else if (this.selectedMarkerMode === this.BY_INSPECTION) {

        this.markerClusterer.clearMarkers()
        this.markerClusterer.addMarkers(this.inspectionMarkersArray)

        for (const marker of this.inspectionMarkersToBounce) {
          this.setMarkerBounce(marker)
        }
        for (const marker of this.projectMarkersToBounce) {
          marker.setVisible(false)
        }
      }
      if (this.activeInfoWindow) {
        this.activeInfoWindow.close()
      }

    },
    async reloadAndRedrawMarkers(inspectionId = null) {
      this.stopBounce()
      await this.loadClusteredMarkers()
      await this.redrawMarkers()
      await this.updateActiveInfoWindowContent()
      if (inspectionId) {
        await this.updateBouncingMarkers(inspectionId)
      }
    },
    updateBouncingMarkers(inspectionId){
      const bouncingMarkerToUpdate = this.projectMarkersToBounce.find(marker => marker.inspectionIds.includes(inspectionId))
      if (bouncingMarkerToUpdate) {
        const updatedMarker = this.projectMarkersArray.find(marker => marker.project.projectName === bouncingMarkerToUpdate.project.projectName)
        bouncingMarkerToUpdate.infoWindowContent = updatedMarker.infoWindowContent
        if (this.activeInfoWindow) {
          this.activeInfoWindow.open({
            anchor: bouncingMarkerToUpdate,
            map: this.map
          })
        }
        updatedMarker.setVisible(false)
      }
    },
    async updateActiveInfoWindowContent() {
      if (this.activeInfoWindow) {
        let updatedMarker

        if (this.activeInfoWindow.projectName) {
          updatedMarker = this.projectMarkersArray.find(marker => marker.project.projectName === this.activeInfoWindow.projectName)
        } else if (this.activeInfoWindow.inspectionId) {
          updatedMarker = this.inspectionMarkersArray.find(marker => marker.inspection.ptracTaskId === this.activeInfoWindow.inspectionId)
        }
        if (!updatedMarker) {
          console.error("Couldnt find corresponding marker to get updated infoWindowContent")
        } else {
          await this.activeInfoWindow.setContent(updatedMarker.infoWindowContent)
          this.activeInfoWindow.open({
            anchor: updatedMarker,
            map: this.map
          })
        }
      }
    },
    loadProjectMarkersForBounce(filterBy) {
      this.projectMarkersToBounce = []

      for (const project of Object.values(this.inspectionsByProject)) {
        if (filterBy.hasOwnProperty("projectName")) {
          if (project.projectName === filterBy.projectName) {
            const marker = this.createProjectMarker(project, true)
            if(marker) {
              this.projectMarkersToBounce.push(marker)
            }

          }
        } else if (filterBy.hasOwnProperty("inspectorId")) {
          for (const inspection of Object.values(project.inspections)) {
            if (inspection.assignedInspector.id === filterBy.inspectorId) {
              const marker = this.createProjectMarker(project, true)
              if (marker) {
                this.projectMarkersToBounce.push(marker)
              }
              break
            }
          }
        }
      }
    },
    loadInspectionMarkersForBounce(filterBy) {
      this.inspectionMarkersToBounce = []

      for (const inspector of Object.values(this.inspectionsByInspector)) {
        for (const inspection of Object.values(inspector.inspections)) {
          if (filterBy.hasOwnProperty("inspectorId")) {
            if (inspection.assignedInspector.id === filterBy.inspectorId) {
              const marker = this.createInspectionMarker(inspector, inspection, true)
              if (marker) {
                this.inspectionMarkersToBounce.push(marker)
              }
            }
          } else if (filterBy.hasOwnProperty("projectName")) {
            if (inspection.projectName === filterBy.projectName) {
              const marker = this.createInspectionMarker(inspector, inspection, true)
              if (marker)
              this.inspectionMarkersToBounce.push(marker)
            }
          }
        }
      }
    },
    createProjectMarker(project, forBounce=false) {
      let inspectionIds = []
      let inspectorIds = []
      for (const inspection of Object.values(project.inspections)) {
        inspectionIds.push(inspection.ptracTaskId)
        inspectorIds.push(inspection.assignedInspector.id)
      }

      const InfoWindowComponent = Vue.extend(ProjectMarkerInfoWindowContent)
      const infoWindowInstance = new InfoWindowComponent({
        propsData: {
          project: project,
          vue: this
        }
      })

      infoWindowInstance.$mount()
      const contentString = infoWindowInstance.$el
      infoWindowInstance.$destroy()

      const markerConfig = {
        // map: this.map,  // dont set in config if I'm loading both sets of markers at init
        type: 'project',
        project: project,
        inspectionIds: inspectionIds,
        inspectorIds: inspectorIds,
        title: project.projectName + " (" + project.inspections.length + (project.inspections.length === 1 ? " inspection)" : " inspections)"),
        position: this.createPositionObjectFromInspection(project.inspections[0]),
        zIndex:1,
        // labels dont animate with the marker - use icon with label instead of writing custom animation
        // label: project.inspections.length < 10 ? project.inspections.length.toString() : '+',
        icon: project.inspections.length < 11 ?
          `https://maps.google.com/mapfiles/kml/paddle/${project.inspections.length.toString()}.png`
          : `https://maps.google.com/mapfiles/kml/paddle/red-stars.png`,
        infoWindowContent: contentString
      }

      if (!markerConfig.position) {
        this.missingCoordinatesErrors.add(project.projectId)
        this.missingCoordinatesErrorsChangeTracker++
        return null
      }

      if (project.inspections[0].project.address.usingLatLngOfCity) {
        this.cityCoordinatesErrors.add(project.projectId)
        this.cityCoordinatesErrorsChangeTracker++
      }

      if (forBounce) {
        markerConfig.map = this.map
      }

      return this.createMarkerFromConfig(markerConfig)
    },
    createInspectionMarker(inspector, inspection, forBounce=false) {
      const InfoWindowComponent = Vue.extend(InspectionMarkerInfoWindowContent)
      const infoWindowInstance = new InfoWindowComponent({
        propsData: {
          inspection: inspection,
          vue: this
        }
      })

      infoWindowInstance.$mount()
      const contentString = infoWindowInstance.$el
      infoWindowInstance.$destroy()

      const markerConfig = {
        // map: this.map, // dont set in config if im loading both sets of markers on init
        type: 'inspection',
        inspection: inspection,
        inspectorId: inspector.inspectorId,
        projectName: inspection.projectName,
        title: inspector.inspectorName + ": " + inspection.inspectionType + " @ " + inspection.projectName,
        position: this.createPositionObjectFromInspection(inspection),
        zIndex: 1,
        infoWindowContent: contentString
      }

      if (!markerConfig.position) {
        this.missingCoordinatesErrors.add(inspection.project.id)
        this.missingCoordinatesErrorsChangeTracker++
        return null
      }

      if (inspection.project.address.usingLatLngOfCity) {
        this.cityCoordinatesErrors.add(inspection.project.id)
        this.cityCoordinatesErrorsChangeTracker++
      }

      if (forBounce) {
        markerConfig.map = this.map
      }

      return this.createMarkerFromConfig(markerConfig)
    },
    createPositionObjectFromInspection(inspection) {
      // inspectionCoordinates shouldn't be null, but checking anyway
      if (inspection.inspectionCoordinates && inspection.inspectionCoordinates[0] && inspection.inspectionCoordinates[1]) {
        const position = {
          lat: Number(inspection.inspectionCoordinates[0]),
          lng: Number(inspection.inspectionCoordinates[1])
        }
        return new this.google.maps.LatLng(position)
      }
      return null
    },
    createMarkerFromConfig(markerConfig) {
      let marker = new this.google.maps.Marker(markerConfig)

      marker.addListener('click', (map) => {
        if (this.activeInfoWindow) {
          this.activeInfoWindow.close()
        }

        this.activeInfoWindow = new this.google.maps.InfoWindow({
          content: marker.infoWindowContent,
          backgroundColor: 'white',
        })

        // For finding marker from activeInfoWindow when reassigning inspection from infoWindow
        if (marker.type === "project") {
          this.activeInfoWindow.projectName = markerConfig.project.projectName
        } else if (marker.type === "inspection") {
          this.activeInfoWindow.inspectionId = markerConfig.inspection.ptracTaskId
        }

        this.activeInfoWindow.open({
          anchor: marker,
          map
        })
      })

      marker.addListener('closeclick', () => {
        this.activeInfoWindow=null
      })

      return marker
    },
    startBounce(row) {
      // Add bounce to both project and inspection markers so that bouncing is preserved as marker type is toggled
      if (row.projectName) {
        // startBounceProjectMarkersByProject(row)
          const projectMarker = this.projectMarkersArray.find(marker => marker.project.projectName === row.projectName)
          if (!projectMarker) {
            console.error("Couldnt find project marker for " + row.projectName)
          } else {
            projectMarker.setVisible(false)
          }

          this.loadProjectMarkersForBounce({"projectName": row.projectName})
          for (const marker of this.projectMarkersToBounce) {
            this.setMarkerBounce(marker)
          }

        // startBounceInspectionMarkersByProject(row)
          const inspectionMarkers = this.inspectionMarkersArray.filter(marker => marker.projectName === row.projectName)
          for (const marker of inspectionMarkers) {
            marker.setVisible(false)
          }

          this.loadInspectionMarkersForBounce({"projectName": row.projectName})
          for (const marker of this.inspectionMarkersToBounce) {
            this.setMarkerBounce(marker)
          }
      } else if (row.inspectorId) {
        // startBounceProjectMarkersByInspector(row)
          const projectMarkers = this.projectMarkersArray.filter(marker => marker.inspectorIds.includes(row.inspectorId))
          for (const marker of projectMarkers) {
            marker.setVisible(false)
          }

          this.loadProjectMarkersForBounce({"inspectorId": row.inspectorId})
          for (const marker of this.projectMarkersToBounce) {
            this.setMarkerBounce(marker)
          }

        // startBounceInspectionMarkersByInspector(row)
          const inspectionMarkers = this.inspectionMarkersArray.filter(marker => marker.inspectorId === row.inspectorId)
          for (const marker of inspectionMarkers) {
            marker.setVisible(false)
          }

          this.loadInspectionMarkersForBounce({"inspectorId": row.inspectorId})
          for (const marker of this.inspectionMarkersToBounce) {
            this.setMarkerBounce(marker)
          }
      }

      this.redrawMarkers()
    },
    stopBounce() {
      // stopBounceProjectMarkers()
      for (const marker of this.projectMarkersToBounce) {
        let clusteredMarker = this.projectMarkersArray.find(cMarker => cMarker.title === marker.title)
        clusteredMarker.setVisible(true)
        marker.setVisible(false)
      }

      //stopBounceInspectionMarkers()
      for (const marker of this.inspectionMarkersToBounce) {
        let clusteredMarker = this.inspectionMarkersArray.find(cMarker => cMarker.title === marker.title)
        if (!clusteredMarker) {
          console.error("Couldnt find inspection marker with title: " + marker.title)
          continue
        }
        clusteredMarker.setVisible(true)
        marker.setVisible(false)
      }
      this.inspectionMarkersToBounce = []
      this.projectMarkersToBounce = []
      this.redrawMarkers()
    },
    setMarkerBounce(marker) {
      marker.setAnimation(this.markerAnimation)
      marker.setZIndex( 999)
      marker.setVisible(true)
    },
    async updateMissingProjectCoordinates() {
      this.repairingErrors = true
      const mergedErrors = new Set([...this.missingCoordinatesErrors, ...this.cityCoordinatesErrors])
      const response = await dispatch('projects/saveProjectsToUpdateCoordinates', [...mergedErrors])
      if (response.status === OK) {
        commit('inspectorRouting/updateInspectionCoordinates', response.data)
      }
      this.missingCoordinatesErrors = new Set
      this.missingCoordinatesErrorsChangeTracker++
      this.cityCoordinatesErrors = new Set
      this.cityCoordinatesErrorsChangeTracker++
      await this.reloadAndRedrawMarkers()
      this.repairingErrors = false
    }
  },
  async mounted() {
    this.selectedMarkerMode = this.BY_PROJECT
    this.errorsDismissed = false
    await this.initGoogleMap()
    this.google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
      this.$eventHub.$emit('mapLoaded', true)
    })
    this.$eventHub.$on('startBounce', this.startBounce)
    this.$eventHub.$on('stopBounce', this.stopBounce)
    this.$eventHub.$on('stopBounceOnAll', this.stopBounce)
    this.$eventHub.$on('assignmentChanged', this.reloadAndRedrawMarkers)

    window.changeAssignment = this.changeAssignment
  },

  beforeDestroy() {
    this.$eventHub.$off('startBounce')
    this.$eventHub.$off('stopBounce')
    this.$eventHub.$off('stopBounceOnAll')
    this.$eventHub.$off('assignmentChanged')
    this.$eventHub.$emit('mapLoaded', false)
  },
}
</script>

<style scoped>

#marker-selection {
  position: absolute;
  top: 25px;
  left: 25px;
  z-index: 10;
}

#marker-error-banner {
  position: absolute;
  top: 75px;
  left: 5%;
  width: 90%;
}

>>>.gm-style-iw {
  padding:20px;
}

</style>
